/**
 * tile-source-links — URL/name/label helpers for per-tile external sources.
 *
 * Split from TileDetailSheet.tsx to keep file sizes bounded (<400 lines each).
 * These are pure functions — no React, no side effects.
 *
 * Immutability: all functions are pure.
 */
import type { Tile } from 'briefpane/schema';

/** Build the level-2 source URL for the given tile type and params. */
export function sourceUrl(tile: Tile): string | null {
  const params = tile.source?.params as Record<string, unknown> | undefined;
  switch (tile.type) {
    case 'weather.current': {
      const lat = params?.lat as number | undefined;
      const lon = params?.lon as number | undefined;
      if (lat !== undefined && lon !== undefined) {
        return `https://openweathermap.org/weathermap?lat=${lat}&lon=${lon}`;
      }
      return 'https://openweathermap.org';
    }
    case 'markets.quote':
    case 'markets.realtime': {
      const syms = params?.symbols as string[] | undefined;
      const sym = syms?.[0];
      return sym ? `https://finance.yahoo.com/quote/${sym}/` : 'https://finance.yahoo.com';
    }
    case 'news.headlines':
      return 'https://gnews.io';
    case 'sports.score': {
      const teamId = params?.teamId as string | undefined;
      return teamId
        ? `https://www.thesportsdb.com/team/${teamId}`
        : 'https://www.thesportsdb.com';
    }
    default:
      return null;
  }
}

/** Human-readable provider name for the detail sheet footer. */
export function sourceName(tile: Tile): string {
  switch (tile.type) {
    case 'weather.current': return 'OpenWeather';
    case 'markets.quote':
    case 'markets.realtime': return 'Yahoo Finance';
    case 'news.headlines': return 'GNews';
    case 'sports.score': return 'TheSportsDB';
    case 'clock.local': return 'System clock';
    default: return tile.source?.provider ?? '';
  }
}

/** CTA label for the level-2 source link. */
export function sourceLinkLabel(tile: Tile): string {
  switch (tile.type) {
    case 'weather.current': return 'View full forecast →';
    case 'markets.quote':
    case 'markets.realtime': return 'View on Yahoo Finance →';
    case 'news.headlines': return 'View more headlines →';
    case 'sports.score': return 'View box score →';
    default: return 'View source →';
  }
}

/** Human-readable data-freshness label. */
export function freshnessLabel(fetchedAt: number | null): string {
  if (fetchedAt === null) return '';
  const ageSec = Math.floor((Date.now() - fetchedAt) / 1000);
  if (ageSec < 60) return 'Updated just now';
  if (ageSec < 3600) return `Updated ${Math.floor(ageSec / 60)}m ago`;
  return `Updated ${Math.floor(ageSec / 3600)}h ago`;
}
